import React, { useMemo } from "react";
import { useSelector } from "react-redux";
import { Route, Switch, useLocation } from "wouter";

import { useUser } from "../../context/UserContext";
import { useCsrf } from "../../context/CsrfContext";
import { useScrollToTop, useTheme } from "../../hooks";
import { Finances } from "../../pages/Finances";
import {
  CompleteProfilePage,
  ForgotPasswordPage,
  LoginPage,
  PrivacyPolicyPage,
  RegisterPage,
  ResetPasswordPage,
  TermsOfServicePage,
  VerifyPage,
} from "../../pages/Auth";
import { TransactionsPage } from "../../pages/Transactions";
import { SavingPage, SavingsPage } from "../../pages/Savings";
import { InvestmentPage, InvestmentsPage } from "../../pages/Investments";
import { LiabilitiesPage, LiabilityPage } from "../../pages/Liabilities";
import { NotFound } from "../../pages/NotFound";
import { WelcomePage } from "../../pages/Welcome";
import { AccountDetailsPage, AccountsPage } from "../../pages/Accounts";
import { AdminShell } from "../AdminShell";
import { RedirectTo } from "../RedirectTo";
import { TopNavbar } from "../TopNavbar";
import {
  Notification,
  NotificationList,
  NotificationPortal,
} from "../Notification";
import { RootState } from "../../redux/store";
import {
  Container,
  MainWrapper,
  NotificationWrapperOver,
  Wrapper,
} from "./styles";
import { LoadingSpinner } from "../LoadingSpinner";
import { SettingsPage } from "../../pages/Settings";

// Types
interface RouteParams {
  id: string;
}

// Constants
const MODAL_ROOT_ID = "modal-root";
const DEFAULT_ID = "0";

// Custom hooks
const useModalElement = () => {
  return useMemo(() => {
    const element = document.getElementById(MODAL_ROOT_ID);
    if (!element) {
      console.warn(`Modal root element with id "${MODAL_ROOT_ID}" not found`);
    }
    return element;
  }, []);
};

const parseId = (id: string = DEFAULT_ID): number => {
  const parsed = Number.parseInt(id, 10);
  return Number.isNaN(parsed) ? 0 : parsed;
};

const AccountDetailsRoute = ({ params }: { params: RouteParams }) => (
  <AccountDetailsPage accountId={parseId(params.id)} />
);
const InvestmentRoute = ({ params }: { params: RouteParams }) => (
  <InvestmentPage id={parseId(params.id)} />
);
const SavingRoute = ({ params }: { params: RouteParams }) => (
  <SavingPage id={parseId(params.id)} />
);
const LiabilityRoute = ({ params }: { params: RouteParams }) => (
  <LiabilityPage id={parseId(params.id)} />
);

const ResetPasswordRoute = () => {
  const [, navigate] = useLocation();

  // Capture the reset token from the URL once, on mount, and keep it only in
  // in-memory React state. The URL is then scrubbed by the effect below so the
  // token can't leak via browser history, back/forward navigation, or the
  // token-bearing Referer header sent when form assets load.
  const [token] = React.useState(() =>
    new URLSearchParams(globalThis.location.search).get("token"),
  );
  const [isUrlScrubbed, setIsUrlScrubbed] = React.useState(
    () => !globalThis.location.search.includes("token="),
  );

  React.useLayoutEffect(() => {
    if (!token) {
      navigate("/login");
      return;
    }

    if (globalThis.location.search.includes("token=")) {
      // Strip ?token=... from the address bar/history without triggering a reload
      // or a new history entry. replaceState does not fire popstate, so the router stays put.
      globalThis.history.replaceState(null, "", globalThis.location.pathname);
    }
    setIsUrlScrubbed(true);
  }, [token, navigate]);

  if (!token || !isUrlScrubbed) return null;

  return <ResetPasswordPage token={token} />;
};

const UnauthenticatedApp = React.memo(
  ({
    notifications,
    getUser,
  }: {
    notifications: Notification[];
    getUser: () => void;
  }) => (
    <div data-theme="light">
      <NotificationWrapperOver>
        <NotificationList notifications={notifications} />
      </NotificationWrapperOver>
      <Switch>
        <Route path="/">
          <WelcomePage />
        </Route>
        <Route path="/login">
          <LoginPage getUser={getUser} />
        </Route>
        <Route path="/terms">
          <TermsOfServicePage />
        </Route>
        <Route path="/privacy">
          <PrivacyPolicyPage />
        </Route>
        <Route path="/register">
          <RegisterPage getUser={getUser} />
        </Route>
        <Route path="/forgot-password">
          <ForgotPasswordPage />
        </Route>
        <Route path="/reset-password">
          <ResetPasswordRoute />
        </Route>
        <Route path="/verify">
          <VerifyPage />
        </Route>
        <Route>
          <NotFound />
        </Route>
      </Switch>
    </div>
  ),
);

UnauthenticatedApp.displayName = "UnauthenticatedApp";

const AuthenticatedApp = React.memo(
  ({
    notifications,
    user,
    endSession,
    getUser,
    modalElement,
  }: {
    notifications: Notification[];
    user: any;
    endSession: () => void;
    getUser: () => void;
    modalElement: HTMLElement | null;
  }) => {
    if (user && !user.dateOfBirth && !user.ageVerificationExempt) {
      return (
        <CompleteProfilePage
          user={user}
          onComplete={getUser}
          onExit={endSession}
        />
      );
    }

    return (
      <Wrapper>
        <NotificationPortal
          notifications={notifications}
          modalElement={modalElement}
        />
        <TopNavbar user={user} endSession={endSession} />
        <MainWrapper>
          <Switch>
            <Route path="/">
              <Finances />
            </Route>
            <Route path="/accounts">
              <AccountsPage />
            </Route>
            <Route path="/liabilities">
              <LiabilitiesPage />
            </Route>
            <Route path="/transactions">
              <TransactionsPage />
            </Route>
            <Route path="/investments">
              <InvestmentsPage />
            </Route>
            <Route path="/savings">
              <SavingsPage />
            </Route>
            <Route path="/reports">
              <RedirectTo to="/settings/reports" />
            </Route>
            <Route path="/settings/:section?">
              {(params) => <SettingsPage section={params.section} />}
            </Route>
            <Route path="/accounts/:id">
              {(params) => <AccountDetailsRoute params={params} />}
            </Route>
            <Route path="/investments/:id">
              {(params) => <InvestmentRoute params={params} />}
            </Route>
            <Route path="/savings/:id">
              {(params) => <SavingRoute params={params} />}
            </Route>
            <Route path="/liabilities/:id">
              {(params) => <LiabilityRoute params={params} />}
            </Route>
            <Route>
              <NotFound />
            </Route>
          </Switch>
        </MainWrapper>
      </Wrapper>
    );
  },
);

AuthenticatedApp.displayName = "AuthenticatedApp";

const App: React.FC = () => {
  const { user, getUser, endSession, isLoading } = useUser();
  const { isInitialized: csrfInitialized, isLoading: csrfLoading } = useCsrf();
  const alerts = useSelector((state: RootState) => state.notifications);
  const modalElement = useModalElement();

  const notifications = useMemo(
    () => alerts?.notifications || [],
    [alerts?.notifications],
  );
  useTheme();
  useScrollToTop();

  if (isLoading || csrfLoading) {
    return (
      <Container>
        <LoadingSpinner size={70} />
      </Container>
    );
  }

  if (!csrfInitialized) {
    console.warn(
      "CSRF token not initialized - state-changing requests may fail",
    );
  }

  if (!user) {
    return (
      <UnauthenticatedApp notifications={notifications} getUser={getUser} />
    );
  }

  if (user.isAdmin) {
    return (
      <AdminShell
        notifications={notifications}
        endSession={endSession}
        modalElement={modalElement}
      />
    );
  }

  return (
    <AuthenticatedApp
      notifications={notifications}
      user={user}
      endSession={endSession}
      getUser={getUser}
      modalElement={modalElement}
    />
  );
};

export default App;
